// PartA3TimeMap.js - Interactive Time Map for Part A3
import React, { useState } from 'react';
import { Input, Button } from 'antd';

const { TextArea } = Input;

const ACTIVITY_COLORS = [
    { name: 'Sleep',   hex: '#BFDBFE' },
    { name: 'Study',   hex: '#FEF3C7' },
    { name: 'Sports',  hex: '#D1FAE5' },
    { name: 'Screen',  hex: '#EDE9FE' },
    { name: 'Family',  hex: '#FCE7F3' },
    { name: 'Chores',  hex: '#FFEDD5' },
    { name: 'Food',    hex: '#FCA5A5' },
    { name: 'Other',   hex: '#E5E7EB' },
];

const SVG_SIZE = 260;
const CX = 130, CY = 130;
const R = 105;         // outer clock face radius
const NUM_R = 88;      // radius for hour numbers
const LABEL_R = 62;    // radius for activity labels inside slices
const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

// Center angle (degrees) for hour h in SVG coords (0=right, clockwise positive)
function hourCenterDeg(h) {
    return (h % 12) * 30 - 90;
}

// SVG pie-slice path for hour h (slice is centered on the hour's position, ±15 deg)
function slicePath(h) {
    const centerDeg = hourCenterDeg(h);
    const s = (centerDeg - 15) * (Math.PI / 180);
    const e = (centerDeg + 15) * (Math.PI / 180);
    const x1 = (CX + R * Math.cos(s)).toFixed(2);
    const y1 = (CY + R * Math.sin(s)).toFixed(2);
    const x2 = (CX + R * Math.cos(e)).toFixed(2);
    const y2 = (CY + R * Math.sin(e)).toFixed(2);
    return `M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2} Z`;
}

function numPos(h) {
    const rad = hourCenterDeg(h) * (Math.PI / 180);
    return { x: CX + NUM_R * Math.cos(rad), y: CY + NUM_R * Math.sin(rad) };
}

function labelPos(h) {
    const rad = hourCenterDeg(h) * (Math.PI / 180);
    return { x: CX + LABEL_R * Math.cos(rad), y: CY + LABEL_R * Math.sin(rad) };
}

const InteractiveClock = ({ period, activities, selectedSlice, onSliceClick }) => {
    const [hovered, setHovered] = useState(null);

    return (
        <svg width={SVG_SIZE} height={SVG_SIZE} viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}>
            {/* Outer border */}
            <circle cx={CX} cy={CY} r={R + 12} fill="white" stroke="#1F2937" strokeWidth="3" />

            {/* Hour slices */}
            {HOURS.map(h => {
                const act = activities.find(a => a.period === period && a.hour === h);
                const isSelected = selectedSlice?.period === period && selectedSlice?.hour === h;
                const isHovered = hovered === h;
                const pos = labelPos(h);
                const shortLabel = act?.label ? act.label.substring(0, 6) : '';

                let fillColor = 'transparent';
                if (act) fillColor = act.color;
                else if (isSelected || isHovered) fillColor = '#FFF7ED';

                return (
                    <g
                        key={h}
                        onClick={() => onSliceClick(period, h)}
                        onMouseEnter={() => setHovered(h)}
                        onMouseLeave={() => setHovered(null)}
                        style={{ cursor: 'pointer' }}
                    >
                        <path
                            d={slicePath(h)}
                            fill={fillColor}
                            stroke="#D1D5DB"
                            strokeWidth="0.8"
                        />
                        {shortLabel && (
                            <text
                                x={pos.x}
                                y={pos.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="7.5"
                                fontWeight="700"
                                fill="#374151"
                                style={{ pointerEvents: 'none' }}
                            >
                                {shortLabel}
                            </text>
                        )}
                    </g>
                );
            })}

            {/* Hour numbers (rendered on top of slices) */}
            {HOURS.map(h => {
                const pos = numPos(h);
                return (
                    <text
                        key={h}
                        x={pos.x}
                        y={pos.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="13"
                        fontWeight="700"
                        fill="#111827"
                        style={{ pointerEvents: 'none' }}
                    >
                        {h}
                    </text>
                );
            })}

            {/* Center dot */}
            <circle cx={CX} cy={CY} r={7} fill="#1F2937" />

            {/* Period label at bottom */}
            <text
                x={CX}
                y={SVG_SIZE - 8}
                textAnchor="middle"
                fontSize="13"
                fontWeight="bold"
                fill="#6B7280"
            >
                {period}
            </text>
        </svg>
    );
};

const PartA3TimeMap = ({ formData, handleInputChange }) => {
    const [selectedSlice, setSelectedSlice] = useState(null); // { period, hour }
    const [editLabel, setEditLabel] = useState('');
    const [editColor, setEditColor] = useState(ACTIVITY_COLORS[0].hex);

    const activities = formData.time_map_activities || [];

    const handleSliceClick = (period, hour) => {
        const existing = activities.find(a => a.period === period && a.hour === hour);
        setSelectedSlice({ period, hour });
        setEditLabel(existing?.label || '');
        setEditColor(existing?.color || ACTIVITY_COLORS[0].hex);
    };

    const handleSave = () => {
        if (!selectedSlice) return;
        const updated = activities.filter(
            a => !(a.period === selectedSlice.period && a.hour === selectedSlice.hour)
        );
        if (editLabel.trim()) {
            updated.push({
                period: selectedSlice.period,
                hour: selectedSlice.hour,
                label: editLabel.trim(),
                color: editColor,
            });
        }
        handleInputChange('time_map_activities', updated);
        setSelectedSlice(null);
    };

    const handleClear = () => {
        if (!selectedSlice) return;
        const updated = activities.filter(
            a => !(a.period === selectedSlice.period && a.hour === selectedSlice.hour)
        );
        handleInputChange('time_map_activities', updated);
        setSelectedSlice(null);
    };

    const endHour = (h) => (h % 12) + 1;

    return (
        <div className="a4-page font-[poppins] bg-white p-8 mx-auto text-[#333333]" style={{ maxWidth: '100%', width: '100%' }}>

            {/* Preferences */}
            <div className="grid grid-cols-2 gap-8 mb-10">
                <div className="bg-[#DBEAFE] p-6 rounded-2xl shadow-sm border border-blue-200 flex flex-col h-full">
                    <label className="block font-bold text-lg mb-4 text-center">
                        One thing I would like to spend <span className="text-[#F97316]">more time</span> on:
                    </label>
                    <TextArea
                        value={formData.spend_more_time_on}
                        onChange={(e) => handleInputChange('spend_more_time_on', e.target.value)}
                        className="flex-1 bg-white border-blue-200 rounded-xl p-4 text-center text-lg"
                        placeholder="Type here..."
                        autoSize={{ minRows: 4 }}
                    />
                </div>
                <div className="bg-[#DBEAFE] p-6 rounded-2xl shadow-sm border border-blue-200 flex flex-col h-full">
                    <label className="block font-bold text-lg mb-4 text-center">
                        One thing I would like to spend <span className="text-[#F97316]">less time</span> on:
                    </label>
                    <TextArea
                        value={formData.spend_less_time_on}
                        onChange={(e) => handleInputChange('spend_less_time_on', e.target.value)}
                        className="flex-1 bg-white border-blue-200 rounded-xl p-4 text-center text-lg"
                        placeholder="Type here..."
                        autoSize={{ minRows: 4 }}
                    />
                </div>
            </div>

            {/* Time Map Header */}
            <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-[#F97316] mb-2">Time Map</h2>
                <p className="font-semibold text-sm mb-2">
                    How do you plan your day? Use the clocks to map the activities you do in a day. Later, think about the following questions:
                </p>
                <ul className="text-sm text-gray-600 mb-2 list-disc list-inside inline-block text-left">
                    <li>Is there any activity you should be spending <strong>more time</strong> on?</li>
                    <li>Is there any activity you should be spending <strong>less time</strong> on?</li>
                </ul>
                <p className="text-sm italic text-gray-500 mt-1">
                    Click on a clock slice to label the activity for that hour. As a follow-up, ask any peer to comment on your schedule.
                </p>
            </div>

            {/* Clocks */}
            <div className="flex justify-center gap-16 mb-4 flex-wrap">
                <div className="flex flex-col items-center">
                    <span className="font-bold text-base text-[#F97316] mb-2">AM — Midnight to Noon</span>
                    <InteractiveClock
                        period="AM"
                        activities={activities}
                        selectedSlice={selectedSlice}
                        onSliceClick={handleSliceClick}
                    />
                </div>
                <div className="flex flex-col items-center">
                    <span className="font-bold text-base text-gray-600 mb-2">PM — Noon to Midnight</span>
                    <InteractiveClock
                        period="PM"
                        activities={activities}
                        selectedSlice={selectedSlice}
                        onSliceClick={handleSliceClick}
                    />
                </div>
            </div>

            {/* Color legend */}
            <div className="flex justify-center gap-3 mb-6 flex-wrap">
                {ACTIVITY_COLORS.map(c => (
                    <span key={c.hex} className="flex items-center gap-1 text-xs text-gray-600">
                        <span className="w-4 h-4 rounded-full inline-block border border-gray-300" style={{ backgroundColor: c.hex }} />
                        {c.name}
                    </span>
                ))}
            </div>

            {/* Inline edit panel */}
            {selectedSlice && (
                <div className="mb-8 bg-orange-50 border border-[#F97316]/40 rounded-xl p-5 max-w-md mx-auto shadow-sm">
                    <h4 className="font-bold text-[#F97316] mb-3 text-base">
                        {selectedSlice.period} &mdash; {selectedSlice.hour}:00 – {endHour(selectedSlice.hour)}:00
                    </h4>

                    <div className="mb-3">
                        <label className="block text-sm font-semibold mb-1">Activity name:</label>
                        <Input
                            value={editLabel}
                            onChange={e => setEditLabel(e.target.value)}
                            placeholder="e.g. Sleep, School, Sports..."
                            maxLength={30}
                            onPressEnter={handleSave}
                            autoFocus
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-semibold mb-2">Color:</label>
                        <div className="flex gap-2 flex-wrap">
                            {ACTIVITY_COLORS.map(c => (
                                <button
                                    key={c.hex}
                                    title={c.name}
                                    onClick={() => setEditColor(c.hex)}
                                    style={{
                                        backgroundColor: c.hex,
                                        borderColor: editColor === c.hex ? '#F97316' : '#D1D5DB',
                                        transform: editColor === c.hex ? 'scale(1.25)' : 'scale(1)',
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        cursor: 'pointer',
                                        transition: 'transform 0.15s',
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <Button
                            type="primary"
                            onClick={handleSave}
                            style={{ backgroundColor: '#F97316', borderColor: '#F97316' }}
                        >
                            Save
                        </Button>
                        <Button danger onClick={handleClear}>
                            Clear
                        </Button>
                        <Button onClick={() => setSelectedSlice(null)}>
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            {/* Follow-up & Reflection */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <h3 className="font-bold text-lg mb-4 text-[#F97316]">Follow-up & Reflection</h3>
                <div className="grid grid-cols-2 gap-6 mb-4">
                    <div>
                        <label className="block font-semibold text-sm mb-2">Activity I should spend MORE time on:</label>
                        <TextArea
                            value={formData.activity_more_time}
                            onChange={(e) => handleInputChange('activity_more_time', e.target.value)}
                            className="bg-white"
                            autoSize={{ minRows: 2 }}
                        />
                    </div>
                    <div>
                        <label className="block font-semibold text-sm mb-2">Activity I should spend LESS time on:</label>
                        <TextArea
                            value={formData.activity_less_time}
                            onChange={(e) => handleInputChange('activity_less_time', e.target.value)}
                            className="bg-white"
                            autoSize={{ minRows: 2 }}
                        />
                    </div>
                </div>
                <div>
                    <label className="block font-semibold text-sm mb-2">Peer feedback on my schedule:</label>
                    <TextArea
                        value={formData.peer_feedback}
                        onChange={(e) => handleInputChange('peer_feedback', e.target.value)}
                        className="bg-white"
                        autoSize={{ minRows: 3 }}
                        placeholder="Ask a peer to review your time map and write their comments here..."
                    />
                </div>
            </div>
        </div>
    );
};

export default PartA3TimeMap;
